import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { formatDateTime } from '../../../lib/format';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { AwaitingCheckIn, HospitalQueue, IdentityMethod } from '@dru/types';
import { CONSENT_METHOD_LABEL, IDENTITY_METHOD_LABEL } from '@dru/types';
import {
  Alert, Button, Checkbox, Dialog, EmptyState, EntityCombobox, ErrorState, InfoTip, Input, StatusBadge, Steps,
  TextSkeleton, cn, type StepSpec,
} from '@dru/ui';
import { Check, UserCheck } from 'lucide-react';
import { useAuth } from '../../../lib/auth';
import { holds, PageHeader } from '../../../layouts/ConsoleShell';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage } from '../../../lib/auth';
import { usePartner, useVenuePrefix } from '../usePartner';
import { VenuePicker, examTypeLabel } from './Queue';
import { PASS_MIN_LENGTH } from '../components/ScanLookup';

/**
 * The check-in desk.
 *
 * Check-in is the moment the platform asserts that this specific person was
 * examined, and everything downstream — the results, the certificate, the QR a
 * gate guard scans — inherits that assertion. So the screen is built around
 * the evidence rather than around the appointment: you pick how identity was
 * established first, and the form then asks for what that method actually
 * requires.
 *
 * The three methods are not interchangeable and the screen does not pretend
 * they are. A phone-number search finds the booking; it proves nothing about
 * who is standing at the counter, and the encounter records that.
 */
export function CheckIn() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const preselected = params.get('appointment') ?? '';
  // From the scan box: the ID card's code (read from its QR), or a pass token.
  const scannedCode = params.get('code') ?? '';
  const scannedPass = params.get('pass') ?? '';

  const { partner, partnerId, setPartner, search, loading: partnersLoading, fixed, error: venueError } = usePartner();
  const { principal, permissions } = useAuth();
  const prefix = useVenuePrefix();

  // A worker who came a day early or late is standing at the desk all the
  // same. The queue's own toggle widens to ±1 day and so does this, because
  // a booking the desk cannot select is a worker sent home.
  const [wide, setWide] = useState(false);

  const queue = useQuery<HospitalQueue>(
    () => (partnerId
      ? api.get(`/encounters/queue?partnerId=${partnerId}&limit=100${wide ? '&days=1' : ''}`)
      : Promise.resolve({ awaitingCheckIn: [], inProgress: [] })),
    [partnerId, wide],
  );

  const [appointmentId, setAppointmentId] = useState(preselected);
  // The employee code on the ID card is how most people arrive: the employee
  // app carries no pass, and the token HR is shown is an optional extra.
  // "Has a pass? Scan it instead" switches to the scan-first desk.
  const [method, setMethod] = useState<IdentityMethod>(scannedPass ? 'QR' : 'EMPLOYEE_CODE');
  const [checkInToken, setCheckInToken] = useState(scannedPass);
  const [employeeCode, setEmployeeCode] = useState(scannedCode.toUpperCase());
  // With a code, the desk still looks at the card: the photo is the proof,
  // the code only says which booking. Without this, typing a code found by
  // name would "verify" anyone.
  const [cardSeen, setCardSeen] = useState(false);
  // The printed ID card's QR holds the employee code, not a pass. Scanned
  // into the pass field it used to read as "no booking matches".
  const [cardNote, setCardNote] = useState(!!scannedCode);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consenting, setConsenting] = useState(false);
  const canCaptureConsent = holds(permissions, 'consent.capture');

  const awaiting = queue.data?.awaitingCheckIn ?? [];
  const [resolved, setResolved] = useState<string | null>(null);

  // Arriving from the queue's Check in: select that booking once the list
  // holds it, widening to ±1 day first when it is yesterday's or tomorrow's.
  // Applied from here, after the list arrives, rather than only as the
  // initial state — the booking picker clears its value when it mounts
  // under StrictMode, which silently dropped the preselection.
  const applied = useRef('');
  useEffect(() => {
    if (!preselected || applied.current === preselected || !queue.data || queue.loading) return;
    if (queue.data.awaitingCheckIn.some((a) => a.appointmentId === preselected)) {
      applied.current = preselected;
      setAppointmentId(preselected);
    } else if (!wide) {
      setWide(true);
    }
  }, [preselected, wide, queue.loading, queue.data]);

  // A scanned pass names its own booking. Once the token is long enough to
  // be one, ask which appointment it is and select it — the desk scans and
  // presses Check in, with nothing to pick.
  useEffect(() => {
    if (method !== 'QR') return;
    const token = checkInToken.trim();
    if (token.length < PASS_MIN_LENGTH) { setResolved(null); return; }
    let live = true;
    api.post<{ id: string }>('/appointments/resolve-token', { checkInToken: token })
      .then((a) => {
        if (!live) return;
        setAppointmentId(a.id);
        const known = awaiting.some((x) => x.appointmentId === a.id);
        // A pass that names a booking the narrow list does not hold is
        // usually the day-early case. Widen once and look again rather than
        // making the desk work out which toggle to press.
        if (!known && !wide) setWide(true);
        setResolved(known ? 'today' : 'other');
      })
      .catch(() => { if (live) setResolved('none'); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInToken, method, awaiting.length, wide]);

  const chosen = useMemo(
    () => awaiting.find((a) => a.appointmentId === appointmentId) ?? null,
    [awaiting, appointmentId],
  );

  /** A short scan in the pass field is the ID card's QR: switch to the card path. */
  function maybeCard(raw: string): boolean {
    const code = raw.trim().toUpperCase();
    if (!code || code.length >= PASS_MIN_LENGTH) return false;
    const hit = awaiting.find((a) => a.employee.employeeCode.toUpperCase() === code);
    if (!hit) return false;
    setMethod('EMPLOYEE_CODE'); setCheckInToken(''); setResolved(null);
    setAppointmentId(hit.appointmentId); setEmployeeCode(hit.employee.employeeCode.toUpperCase());
    setCardNote(true); setCardSeen(false); setError(null);
    return true;
  }

  // Somebody already on the floor is not checked in twice: the desk opens
  // their visit instead.
  const onFloor = useMemo(() => {
    if (!chosen) return null;
    const code = chosen.employee.employeeCode.toUpperCase();
    return (queue.data?.inProgress ?? []).find((e) => e.employee.employeeCode.toUpperCase() === code) ?? null;
  }, [chosen, queue.data?.inProgress]);
  const codeTyped = employeeCode.trim().toUpperCase();
  const codeMismatch = method === 'EMPLOYEE_CODE' && !!chosen && codeTyped.length > 0
    && codeTyped !== chosen.employee.employeeCode.toUpperCase();

  // Mirrors the server's refinement. The API is the authority; this exists so
  // the desk is told what is missing before the worker is turned away.
  // A pass that resolved to some other booking is not proof of this one.
  const identityReady = method === 'QR'
    ? checkInToken.trim().length >= PASS_MIN_LENGTH && resolved !== 'none'
    : codeTyped.length > 0 && !codeMismatch && cardSeen;
  // The server refuses a check-in without consent, so the button waits for it
  // too — and says so, instead of failing after the press.
  const consentReady = chosen ? chosen.consentReady : appointmentId !== '';
  const ready = appointmentId !== '' && consentReady && identityReady && !onFloor;
  const blocker = appointmentId === '' ? (method === 'QR' ? 'Scan the employee’s pass.' : 'Choose the booking first.')
    : onFloor ? 'Already checked in — open their visit instead.'
      : !consentReady ? 'Consent has to be recorded before check-in.'
        : !identityReady ? (method === 'QR' ? 'Scan the employee’s pass.'
          : !codeTyped ? 'Type the code from the employee’s ID card.'
            : codeMismatch ? 'The code does not match this booking.'
              : 'Confirm you have seen their ID card.')
          : null;

  // A scanned pass names the booking AND proves who is holding it, so with a
  // pass the desk has one thing to do — scan — and the booking is not a step
  // of its own. Without one, the booking is found by name or code and the ID
  // card's code is the proof.
  const scanning = method === 'QR';
  const steps: StepSpec[] = scanning
    ? [
        {
          key: 'pass', label: 'Scan pass',
          state: appointmentId && identityReady ? 'done' : 'current',
          detail: chosen && identityReady ? chosen.employee.fullName : undefined,
        },
        {
          key: 'consent', label: 'Consent',
          state: !appointmentId ? 'upcoming' : consentReady ? 'done' : 'blocked',
          detail: appointmentId && !consentReady ? 'Not recorded' : undefined,
        },
        { key: 'go', label: 'Check in', state: ready ? 'current' : 'upcoming' },
      ]
    : [
        { key: 'booking', label: 'Booking', state: appointmentId ? 'done' : 'current', detail: chosen ? chosen.employee.fullName : undefined },
        {
          key: 'consent', label: 'Consent',
          state: !appointmentId ? 'upcoming' : consentReady ? 'done' : 'blocked',
          detail: appointmentId && !consentReady ? 'Not recorded' : undefined,
        },
        { key: 'identity', label: 'Employee code', state: !appointmentId || !consentReady ? 'upcoming' : identityReady ? 'done' : 'current' },
        { key: 'go', label: 'Check in', state: ready ? 'current' : 'upcoming' },
      ];

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { identityMethod: method };
      if (method === 'QR') body.checkInToken = checkInToken.trim();
      if (method === 'EMPLOYEE_CODE') body.employeeCode = employeeCode.trim().toUpperCase();

      const encounter = await api.post<{ id: string }>(
        `/appointments/${appointmentId}/check-in`, body, crypto.randomUUID(),
      );
      nav(`${prefix}/encounter/${encounter.id}`);
    } catch (e) {
      // "Capture consent on the spot", "the slot is on the 12th", "the token
      // has already been used" — the API writes these for the person at the
      // desk, and they are more useful than anything this screen could invent.
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (partnersLoading) return <TextSkeleton lines={6} />;

  return (
    <div className="max-w-3xl space-y-5">
      <PageHeader
        className="mb-0"
        title="Check in an employee"
        hint="Scan the employee’s pass: it selects the booking and confirms who is at the desk. Without a pass, find the booking and check the code on their ID card."
        actions={(
          <>
            {/* A venue login's venue is already named in the top bar. */}
            {!principal?.partner && (
              <VenuePicker partner={partner} onChange={setPartner} search={search} fixed={fixed} />
            )}
            <Button variant="secondary" size="sm" onClick={() => nav(`${prefix}/queue`)}>Back to the queue</Button>
          </>
        )}
      />

      {!partnerId && venueError ? (
        <ErrorState title="Could not load your venues" detail={venueError} />
      ) : !partnerId ? (
        <EmptyState
          title="No venue selected"
          hint="Choose the hospital or camp you are working at to see who is booked in."
        />
      ) : queue.error ? (
        <ErrorState title="Could not load today’s bookings" detail={queue.error} onRetry={queue.reload} />
      ) : !queue.loading && !awaiting.length ? (
        <EmptyState
          title={wide
            ? 'Nobody is booked at this venue from yesterday to tomorrow'
            : 'Nobody is left to check in today at this venue'}
          hint={wide
            ? 'A booking further out than a day is a different visit — HR moves the slot before the employee can be checked in here.'
            : 'Check-in works against a booking for today. If the employee has come a day early or late, widen the search.'}
          action={(
            <div className="flex flex-wrap justify-center gap-2">
              {!wide && (
                <Button onClick={() => setWide(true)}>Include yesterday and tomorrow</Button>
              )}
              <Button variant="secondary" onClick={() => nav(`${prefix}/queue`)}>Back to the queue</Button>
            </div>
          )}
        />
      ) : (
        <div className="rounded-card border border-neutral-200 bg-white shadow-card">
          <div className="border-b border-neutral-100 px-5 py-4">
            <Steps steps={steps} label="Check-in progress" orientation="horizontal" />
          </div>

          <div className="space-y-6 p-5">
            {scanning ? (
              <>
                {/* 1 — The pass. Scanning it selects the booking and proves
                    who is at the desk, in one act. */}
                <DeskStep n={1} title="Scan the pass">
                  <label className="block">
                    <span className="sr-only">Scanned token</span>
                    <Input
                      value={checkInToken}
                      onChange={(e) => { setCheckInToken(e.target.value); setCardNote(false); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); maybeCard(checkInToken); } }}
                      onBlur={() => maybeCard(checkInToken)}
                      autoFocus
                      spellCheck={false}
                      autoComplete="off"
                      placeholder="Scan the employee’s QR, or type the token"
                      className="font-mono"
                      aria-label="Scanned token"
                      aria-describedby="token-state"
                    />
                  </label>
                  <span id="token-state" className="mt-1 block text-caption text-neutral-500" aria-live="polite">
                    {resolved === 'today' && chosen
                      ? <span className="font-medium text-covered-600">Pass recognised — booking selected and identity verified.</span>
                      : resolved === 'other'
                        ? <span className="text-overdue-600">This pass belongs to a booking that is not in this venue&rsquo;s list — it may be at another venue, or more than a day from today.</span>
                        : resolved === 'none'
                          ? <span className="text-overdue-600">No booking matches this pass. It may have been used already, or the slot was moved. If it is the ID card&rsquo;s QR, press Enter to use it as the employee code.</span>
                          : chosen
                            ? 'Opened from the queue. Scan their pass to confirm it is them.'
                            : 'The pass names the booking and proves who is holding it. It works once.'}
                  </span>
                  {chosen && <BookingSummary booking={chosen} />}
                  {onFloor && <AlreadyIn encounterId={onFloor.encounterId} name={onFloor.employee.fullName} prefix={prefix} />}
                  <button
                    type="button"
                    onClick={() => { setMethod('EMPLOYEE_CODE'); setError(null); }}
                    className="mt-3 inline-flex min-h-6 items-center tap-area text-small font-medium text-primary-700 hover:underline"
                  >
                    No pass? Find the booking and check the ID card instead
                  </button>
                </DeskStep>

                <DeskStep n={2} title="Consent" muted={!appointmentId}>
                  <ConsentStep chosen={chosen} appointmentId={appointmentId}
                    onCapture={canCaptureConsent ? () => setConsenting(true) : undefined} />
                </DeskStep>
              </>
            ) : (
              <>
                {/* 1 — Booking.
                    Searched, not scrolled. This is one venue's day, so the whole
                    list is already in hand — but a camp day is a thousand
                    bookings, and the desk is looking for the person standing in
                    front of them by name or code. */}
                <DeskStep n={1} title="Booking">
                  <EntityCombobox<AwaitingCheckIn>
                    label="Whose booking?"
                    required
                    value={chosen}
                    onChange={(a) => { setAppointmentId(a?.appointmentId ?? ''); setError(null); setCardSeen(false); }}
                    fetchOptions={async (term) => {
                      const q = term.trim().toLowerCase();
                      return {
                        items: awaiting.filter((a) => (
                          a.employee.fullName.toLowerCase().includes(q)
                          || a.employee.employeeCode.toLowerCase().includes(q)
                        )),
                        nextCursor: null,
                      };
                    }}
                    getKey={(a) => a.appointmentId}
                    minChars={0}
                    placeholder={wide ? 'Choose the booking…' : 'Choose today’s booking…'}
                    searchPlaceholder="Name or employee code"
                    emptyMessage={wide
                      ? 'Nobody booked between yesterday and tomorrow matches that.'
                      : 'Nobody booked today matches that.'}
                    hint={queue.loading ? 'Loading bookings…' : `${awaiting.length} still to check in, ${wide ? 'yesterday to tomorrow' : 'booked today'}.`}
                    // Only the venue is a cascade parent. The combobox clears its
                    // value when a dep changes, and the list growing — the day's
                    // bookings arriving, or widening to ±1 day for a pass that was
                    // just scanned — used to wipe the booking the desk (or the
                    // queue's Check in link) had just selected.
                    deps={[partnerId]}
                    renderOption={(a) => (
                      <>
                        <span className="block font-medium text-neutral-900">
                          {a.employee.fullName}
                        </span>
                        <span className="block font-mono text-caption font-normal text-neutral-500">
                          {a.employee.employeeCode} · {slotLabel(a.scheduledFor)}{a.branch ? ` · ${a.branch}` : ''}
                        </span>
                      </>
                    )}
                    renderValue={(a) => `${a.employee.fullName} — ${a.employee.employeeCode}`}
                  />
                  <Checkbox
                    className="mt-2 text-small"
                    label="Include yesterday and tomorrow (employee came a day early or late)"
                    checked={wide}
                    onChange={(e) => setWide(e.target.checked)}
                  />
                  {chosen && <BookingSummary booking={chosen} />}
                  {onFloor && <AlreadyIn encounterId={onFloor.encounterId} name={onFloor.employee.fullName} prefix={prefix} />}
                  <button
                    type="button"
                    onClick={() => { setMethod('QR'); setError(null); }}
                    className="mt-3 inline-flex min-h-6 items-center tap-area text-small font-medium text-primary-700 hover:underline"
                  >
                    Has a pass? Scan it instead
                  </button>
                </DeskStep>

                <DeskStep n={2} title="Consent" muted={!appointmentId}>
                  <ConsentStep chosen={chosen} appointmentId={appointmentId}
                    onCapture={canCaptureConsent ? () => setConsenting(true) : undefined} />
                </DeskStep>

                {/* 3 — Identity, from the ID card. Phone search is not offered:
                    the server refuses a check-in whose identity was not
                    verified. */}
                <DeskStep n={3} title="Employee code" muted={!appointmentId}>
                  <label className="block">
                    <span className="inline-flex items-center gap-1 text-caption text-neutral-700">
                      {IDENTITY_METHOD_LABEL.EMPLOYEE_CODE}
                      <InfoTip label="About the employee code">{METHOD_NOTE.EMPLOYEE_CODE}</InfoTip>
                    </span>
                    <Input
                      value={employeeCode}
                      onChange={(e) => { setEmployeeCode(e.target.value); setCardNote(false); }}
                      spellCheck={false}
                      autoComplete="off"
                      placeholder="Scan the ID card&rsquo;s QR, or read the code from it"
                      className="mt-1 font-mono uppercase"
                      invalid={codeMismatch}
                      aria-describedby="code-state"
                    />
                  </label>
                  <span id="code-state" className="mt-1 block text-caption" aria-live="polite">
                    {codeMismatch
                      ? <span className="text-overdue-600">This code is not {chosen?.employee.fullName}&rsquo;s ({chosen?.employee.employeeCode}). Check the card and the booking.</span>
                      : cardNote
                        ? <span className="text-neutral-600">Read from the employee&rsquo;s ID card QR. That card only names the employee; it is not an appointment pass.</span>
                        : <span className="text-neutral-500">The printed ID card&rsquo;s QR holds this code.</span>}
                  </span>
                  <Checkbox
                    className="mt-3 text-small"
                    label={chosen
                      ? `I have seen the ID card, and the photo matches the person at the desk (${chosen.employee.fullName})`
                      : 'I have seen the ID card, and the photo matches the person at the desk'}
                    checked={cardSeen}
                    onChange={(e) => setCardSeen(e.target.checked)}
                    disabled={!appointmentId}
                  />
                </DeskStep>
              </>
            )}
          </div>

          {/* Held at the foot of a phone's screen: the button is under three
              steps, and the desk presses it with the worker standing there. */}
          <div className="space-y-3 border-t border-neutral-100 bg-neutral-50 px-5 py-4 max-md:sticky max-md:bottom-0 max-md:z-10">
            {error && <Alert tone="danger">{error}</Alert>}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-small text-neutral-600" aria-live="polite">
                {blocker ?? (chosen
                  ? `Ready. Checking in opens ${chosen.employee.fullName}’s encounter for the stations.`
                  : 'Ready. Checking in opens the encounter for the stations.')}
              </p>
              <Button onClick={submit} loading={busy} disabled={!ready} icon={<UserCheck className="h-4 w-4" aria-hidden />}>
                {chosen ? `Check in ${firstName(chosen.employee.fullName)}` : 'Check in'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {consenting && chosen && (
        <DeskConsentDialog
          appointment={chosen}
          onClose={() => setConsenting(false)}
          onDone={() => { setConsenting(false); queue.reload(); }}
        />
      )}
    </div>
  );
}

/** The person is already on the floor: one visit, not two. */
function AlreadyIn({ encounterId, name, prefix }: { encounterId: string; name: string; prefix: string }) {
  return (
    <div role="note" className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-input border border-info-600/25 bg-info-100 px-3 py-2 text-small text-info-600">
      <span className="min-w-0 flex-[1_1_14rem]"><strong>{name} is already checked in.</strong> Open the same visit; a second check-in is not needed.</span>
      <Link to={`${prefix}/encounter/${encounterId}`} className="inline-flex h-8 items-center rounded-btn bg-primary-600 px-3 text-small font-semibold text-white hover:bg-primary-700 max-md:h-10">
        Open their visit
      </Link>
    </div>
  );
}

function firstName(full: string): string {
  return full.trim().split(/\s+/)[0] ?? full;
}

const SLOT_TIME = new Intl.DateTimeFormat('en-IN', { hour: 'numeric', minute: '2-digit' });
const SLOT_DAY = new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
/** "10:00 am", or "Fri 25 Sep, 10:00 am" when it is not today's. */
function slotLabel(v: string): string {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toDateString() === new Date().toDateString()
    ? SLOT_TIME.format(d) : `${SLOT_DAY.format(d)}, ${SLOT_TIME.format(d)}`;
}

/** One numbered part of the desk's form. */
function DeskStep({ n, title, muted, children }: { n: number; title: string; muted?: boolean; children: ReactNode }) {
  return (
    <section aria-labelledby={`desk-step-${n}`} className={cn(muted && 'opacity-70')}>
      <h2 id={`desk-step-${n}`} className="mb-2 flex items-center gap-2 text-h4 text-neutral-900">
        <span aria-hidden className="flex h-6 w-6 items-center justify-center rounded-full bg-neutral-100 text-caption font-semibold text-neutral-700">{n}</span>
        {title}
      </h2>
      {children}
    </section>
  );
}

/** Who, when and what — the booking the desk is about to check in. */
function BookingSummary({ booking: chosen }: { booking: AwaitingCheckIn }) {
  return (
    <>
      <dl className="mt-3 grid gap-x-6 gap-y-2 rounded-input border border-neutral-200 bg-neutral-50 px-4 py-3 sm:grid-cols-3">
        <div className="min-w-0">
          <dt className="text-caption text-neutral-500">Employee</dt>
          <dd className="truncate text-small font-medium text-neutral-900">{chosen.employee.fullName}</dd>
          <dd className="font-mono text-caption text-neutral-600">{chosen.employee.employeeCode}</dd>
        </div>
        <div>
          <dt className="text-caption text-neutral-500">Slot</dt>
          <dd className="text-small text-neutral-900">{slotLabel(chosen.scheduledFor)}</dd>
        </div>
        {chosen.branch && (
          <div>
            <dt className="text-caption text-neutral-500">Booked at</dt>
            <dd className="text-small text-neutral-900">{chosen.branch}</dd>
          </div>
        )}
        <div>
          <dt className="text-caption text-neutral-500">Examination</dt>
          <dd className="text-small text-neutral-900">{examTypeLabel(chosen.examinationType)}</dd>
        </div>
      </dl>
      <OffDayNotice scheduledFor={chosen.scheduledFor} />
    </>
  );
}

function ConsentStep({ chosen, appointmentId, onCapture }: {
  chosen: AwaitingCheckIn | null; appointmentId: string; onCapture?: () => void;
}) {
  if (!appointmentId) return <p className="text-small text-neutral-500">Shown once the booking is known.</p>;
  if (chosen) return <ConsentNotice appointment={chosen} onCapture={onCapture} />;
  return (
    <p className="text-small text-neutral-600">
      The scanned pass names a booking that is not in this venue&rsquo;s list, so its consent cannot be shown here.
    </p>
  );
}

/**
 * Said out loud when the booking is not today's.
 *
 * Reception can take a worker a day either side, but they should know that is
 * what they are doing — and that the slot moves to today, so the day's list
 * at this venue matches who was actually examined.
 */
function OffDayNotice({ scheduledFor }: { scheduledFor: string }) {
  const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const offset = Math.round((midnight(new Date(scheduledFor)) - midnight(new Date())) / 86_400_000);
  if (offset === 0) return null;

  const booked = new Date(scheduledFor).toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long',
  });
  return (
    <div
      role="note"
      className="mt-3 rounded-input border border-dueSoon-600/25 bg-dueSoon-100 px-3 py-2 text-caption text-dueSoon-600"
    >
      <strong>
        This booking is for {booked} — {offset > 0 ? 'they have come early' : 'the slot has passed'}.
      </strong>{' '}
      You can still check them in. The slot moves to today so the venue's list
      shows who was actually examined, and the booked day stays on the record.
    </div>
  );
}

const METHOD_NOTE: Record<IdentityMethod, string> = {
  QR: 'Strongest. The token was issued to this employee for this slot and can only be used once.',
  EMPLOYEE_CODE: 'The code on the employee’s card, checked against the booking.',
  PHONE_SEARCH: 'Fallback when the employee has neither. Recorded as unverified identity.',
};

function ConsentNotice({ appointment, onCapture }: {
  appointment: AwaitingCheckIn; onCapture?: () => void;
}) {
  if (appointment.consentReady) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-small text-neutral-700">
        <StatusBadge tone="covered"><Check className="h-3 w-3" aria-hidden />Consent on record</StatusBadge>
        <span>{appointment.employee.fullName} has consented to this examination.</span>
      </div>
    );
  }
  return (
    <div
      role="note"
      className="flex flex-wrap items-center justify-between gap-3 rounded-input border border-dueSoon-600/25 bg-dueSoon-100 px-3 py-2.5 text-small text-dueSoon-600"
    >
      <span className="min-w-0 flex-[1_1_16rem]">
        <strong>Consent is not recorded yet.</strong> Check-in waits for it. Take it
        here — read the notice to the employee, or have them sign on paper — rather
        than sending them home.
        {!onCapture && ' Ask a colleague who can record consent at the desk.'}
      </span>
      {onCapture && <Button size="sm" onClick={onCapture}>Record consent…</Button>}
    </div>
  );
}


interface DeskNotice {
  employee: { fullName: string; employeeCode: string; preferredLocale: string | null };
  template: {
    code: string; version: number; servedLocale: string; fellBack: boolean;
    title: string; body: string; agreeLabel: string; declineLabel: string;
  };
  availableLocales: string[];
  alreadyRecorded: { id: string; acceptedAt: string; method: string } | null;
}

const LOCALE_NAME: Record<string, string> = { en: 'English', hi: 'हिन्दी', gu: 'ગુજરાતી' };

/**
 * Consent taken at reception.
 *
 * The same notice the worker's app shows, read to them or signed on paper,
 * and recorded against the exact version and language that was on this
 * screen — the server refuses a mismatch. The method is a fact about how it
 * happened, and it is written on the record for the physician to see.
 */
function DeskConsentDialog({ appointment, onClose, onDone }: {
  appointment: AwaitingCheckIn; onClose: () => void; onDone: () => void;
}) {
  const [locale, setLocale] = useState<string | null>(null);
  const [method, setMethod] = useState<'RECEPTION' | 'PAPER_SCAN'>('RECEPTION');
  const [signatureRef, setSignatureRef] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const notice = useQuery<DeskNotice>(
    () => api.get(`/appointments/${appointment.appointmentId}/consent-notice${locale ? `?locale=${locale}` : ''}`),
    [appointment.appointmentId, locale],
  );
  const n = notice.data;
  // First render follows the worker's own language; the picker overrides.
  useEffect(() => {
    if (n && locale === null && n.employee.preferredLocale && n.employee.preferredLocale !== n.template.servedLocale
        && n.availableLocales.includes(n.employee.preferredLocale)) {
      setLocale(n.employee.preferredLocale);
    }
  }, [n, locale]);

  async function record() {
    if (!n) return;
    setBusy(true); setError(null);
    try {
      await api.post(`/appointments/${appointment.appointmentId}/consent`, {
        templateCode: n.template.code,
        templateVersion: n.template.version,
        locale: n.template.servedLocale,
        method,
        ...(signatureRef.trim() ? { signatureRef: signatureRef.trim() } : {}),
      }, crypto.randomUUID());
      onDone();
    } catch (e) { setError(errorMessage(e)); notice.reload(); setConfirmed(false); }
    finally { setBusy(false); }
  }

  return (
    <Dialog title={`Consent — ${appointment.employee.fullName}`} onClose={onClose} wide>
      {notice.error && <Alert className="mt-2">{notice.error}</Alert>}
      {!n && !notice.error && <p className="mt-2 text-sm text-neutral-500">Loading the notice…</p>}
      {n?.alreadyRecorded && (
        <Alert tone="success" className="mt-2" title="Already on record">
          Consent was recorded {formatDateTime(n.alreadyRecorded.acceptedAt)} ({(CONSENT_METHOD_LABEL[n.alreadyRecorded.method] ?? n.alreadyRecorded.method).toLowerCase()}). Nothing more is needed.
        </Alert>
      )}
      {n && !n.alreadyRecorded && (
        <div className="mt-3 space-y-3">
          <div role="group" aria-label="Language of the notice" className="flex flex-wrap gap-2">
            {n.availableLocales.map((l) => (
              <button key={l} type="button" onClick={() => { setLocale(l); setConfirmed(false); }}
                aria-pressed={n.template.servedLocale === l}
                className={cn('rounded-full px-3 py-1 text-xs font-medium',
                  n.template.servedLocale === l ? 'bg-primary-600 text-white' : 'bg-neutral-100 text-neutral-700')}>
                {LOCALE_NAME[l] ?? l}
              </button>
            ))}
          </div>
          {n.template.fellBack && (
            <p className="rounded bg-neutral-100 px-3 py-2 text-xs text-neutral-700">
              Not published in that language yet; the English notice is what the employee is agreeing to.
            </p>
          )}
          <h3 className="text-sm font-semibold text-neutral-900">{n.template.title}</h3>
          <div className="max-h-64 space-y-2 overflow-y-auto rounded border border-neutral-200 p-3 text-sm leading-relaxed text-neutral-800">
            {n.template.body.split('\n\n').map((para, i) => <p key={i}>{para.trim()}</p>)}
          </div>
          <fieldset>
            <legend className="text-xs font-medium text-neutral-700">How was it taken?</legend>
            <div className="mt-1 flex flex-wrap gap-2">
              {([['RECEPTION', 'Read to the employee at the desk, they agreed'], ['PAPER_SCAN', 'Signed on paper, kept on file']] as const).map(([m, label]) => (
                <label key={m} className={cn('flex cursor-pointer items-center gap-2 rounded border px-3 py-2 text-sm',
                  method === m ? 'border-primary-500 bg-primary-50' : 'border-neutral-200')}>
                  <input type="radio" name="consentMethod" checked={method === m} onChange={() => setMethod(m)} />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>
          {method === 'PAPER_SCAN' && (
            <label className="block">
              <span className="text-xs font-medium text-neutral-700">Paper form reference</span>
              <Input value={signatureRef} onChange={(e) => setSignatureRef(e.target.value)} placeholder="Register page or file number" className="mt-1" />
            </label>
          )}
          <label className="flex items-start gap-2 text-sm text-neutral-800">
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-1" />
            {appointment.employee.fullName} has read or been read this notice, in {LOCALE_NAME[n.template.servedLocale] ?? n.template.servedLocale}, and agreed.
          </label>
          <p className="text-caption text-neutral-500">{n.template.code} · version {n.template.version}</p>
        </div>
      )}
      {error && <Alert className="mt-3">{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>{n?.alreadyRecorded ? 'Close' : 'Cancel'}</Button>
        {n && !n.alreadyRecorded && (
          <Button onClick={record} loading={busy} disabled={!confirmed}>Record consent</Button>
        )}
      </div>
    </Dialog>
  );
}
